import { civiliteObject } from "src/app/shared/models/model/concretes/shared/civility.model";
import { format, parse } from "date-fns";
import { QuotationSummaryResponse } from "../models/model/concretes/shared/quotation-summary.model";
import { DevisDocumentType } from "../constants/quotation-document-type.constants";
import {
  CountryVm,
  PhonePrefixesVm,
  CountriesService,
  PhonePrefixesService,
  PolicyOverviewVm,
} from "src/app/api";
import { firstValueFrom, forkJoin, lastValueFrom } from "rxjs";
import { getDeclarationState } from "./riskitem-util";
import { Avenant } from "src/app/api/model/avenant";
import { EndorsementService } from "src/app/api/api/endorsement.service";
import { Guid } from "guid-typescript";
import { AvenantOverviewResponse } from "src/app/api/model/avenantOverviewResponse";

//#region Devis
export function dispatchDeclaration(
  store: any,
  dataDevis: QuotationSummaryResponse,
) {
  store.updateDeclaration(getDeclarationState(dataDevis));
}

export function dispatchDevis(store: any, dataDevis: QuotationSummaryResponse) {
  store.updateDevis({
    NumeroDevis: dataDevis.reference,
    DevisId: dataDevis.quotationId,
    DevisStatus: dataDevis.quotationStateId,
    GDAPositif: !!dataDevis.clientInformations.gdaTypeId,
    PPEPositif: dataDevis.clientInformations.isPpe,
    PrimeMensuelle: dataDevis.summaryInformation.monthlyPremiumTTC,
    PrimeAnnuelle: dataDevis.summaryInformation.annualPremiumTTC,
    AgreementId: dataDevis.agreementId,
    DocumentDevis: {
      documentId: dataDevis.summaryInformation.quotationFileId.toString(),
      documentTypeCode: DevisDocumentType.DEV,
    },
    DocumentFIC: {
      documentId: dataDevis.summaryInformation.ficFileId.toString(),
      documentTypeCode: DevisDocumentType.FIC,
    },
    DocumentInfo: {
      documentId:
        dataDevis.summaryInformation.productInformationFileId.toString(),
      documentTypeCode: DevisDocumentType.DIPA,
    },
    DocumentCG: {
      documentId:
        dataDevis.summaryInformation.generalConditionFileId.toString(),
      documentTypeCode: DevisDocumentType.CG,
    },
    ClientAdvisorFullName: dataDevis.summaryInformation.clientAdvisorFullName,
  });
}

export function dispatchOffer(store: any, dataDevis: QuotationSummaryResponse) {
  store.updateReferenceDevis(dataDevis.reference);
  store.updateMensualPrice(
    dataDevis.summaryInformation.monthlyPriceSocleTTC.toFixed(2),
  );
  store.updateSelectedOffer(
    dataDevis.formulasAndOptionsInformations.formulaCode,
  );
  store.updateCouponCode(dataDevis.summaryInformation.promotionalCode);
  store.updateTypePromotion(dataDevis.summaryInformation.promotionalCodeType);
}

export async function dispatchSubscriber(
  store: any,
  countriesService: CountriesService,
  phonePrefixesService: PhonePrefixesService,
  dataDevis: QuotationSummaryResponse,
) {
  const parsedDate = parse(
    dataDevis.clientInformations.birthDate,
    "yyyy-MM-dd",
    new Date(),
  );
  const birth_country = await getCountryByName(
    countriesService,
    dataDevis.clientInformations.birthCountry,
  );

  const nationality = await getCountryByName(
    countriesService,
    dataDevis.clientInformations.nationality,
  );

  let phone_prefix = await getPhonePrefixesByName(
    phonePrefixesService,
    dataDevis.clientInformations.phonePrefix,
  );
  if (!phone_prefix) {
    phone_prefix = store.subscriber().phone_prefix.indicator;
  }

  store.updateSubscriber({
    civility:
      civiliteObject.find(
        (m) => m.text === dataDevis.clientInformations.civility,
      ) ?? null,
    lastname: dataDevis.clientInformations.lastName,
    firstname: dataDevis.clientInformations.firstName,
    email: dataDevis.clientInformations.email,
    birth_date: format(parsedDate, "dd/MM/yyyy HH:mm:ss"),
    birth_postal_code: dataDevis.clientInformations.zipCode,
    birth_city: dataDevis.clientInformations.birthCity,
    // Preserve existing values if not well retrived from name or code
    birth_country: birth_country ?? store.subscriber().birth_country,
    nationality: nationality ?? store.subscriber().nationality,
    phone_prefix: {
      indicator: phone_prefix,
      phone: dataDevis.clientInformations.cellPhone ?? "",
      phone_consent: false,
      marketing_consent: false,
    },
    is_client: dataDevis.clientInformations.isEnrolled,
  });
}
//#endregion

//#region Contrat
export function dispatchPolicy(store: any, dataPolicy: PolicyOverviewVm) {
  store.updateContract({
    RefContrat: dataPolicy.reference,
    NumeroContrat: dataPolicy.agreementId?.toString(),
  });
}

export function dispatchSubscriber_C(store: any, dataPolicy: PolicyOverviewVm) {
  store.updateSubscriber({
    email: dataPolicy.memberEmail,
  });
}

export function dispatchPayment(store: any, dataPolicy: PolicyOverviewVm) {
  store.updatePhonePrefixSubscriber({
    indicator: { prefix: dataPolicy.memberPhonePrefix },
    phone: dataPolicy.memberPhoneNumber,
    phone_consent: false,
    marketing_consent: false,
  });
  store.updatePhonePrefixDirectDebit({
    indicator: { prefix: dataPolicy.memberPaymentPhonePrefix },
    phone: dataPolicy.memberPaymentPhoneNumber,
    phone_consent: false,
    marketing_consent: false,
  });
}

export function dispatchDevis_C(store: any, dataPolicy: PolicyOverviewVm) {
  store.updateDevis({
    DevisId: dataPolicy.quotationId,
    AgreementId: dataPolicy.agreementId,
  });
}

export function dispatchAvenant(store: any, dataAvenant: Avenant[]) {
  console.log("Dispatching endorsement data:", dataAvenant);
  const endorsementUpdates = dataAvenant.map((dataAvenant) => ({
    NumeroContrat: dataAvenant.agreementId,
    RefContrat: dataAvenant.reference,
    ClientAdvisorFullName: "",
    productName: dataAvenant.productName,
    correlationId: dataAvenant.correlationId,
    isChecked: false,
  }));
  store.updateAvenant({
    Avenants: endorsementUpdates,
  });
}
//#endregion

async function getCountryByName(
  countriesService: CountriesService,
  nameOrCode: string | null | undefined,
): Promise<CountryVm | null> {
  if (!nameOrCode) return null;
  const countries = await firstValueFrom(countriesService.apiCountriesGet());
  return (
    countries.find(
      (c) => c.countryNameFr === nameOrCode || c.countryCode === nameOrCode,
    ) ?? null
  );
}

async function getPhonePrefixesByName(
  phonePrefixesService: PhonePrefixesService,
  prefix: string,
): Promise<PhonePrefixesVm | null> {
  const phonePrefixes = await firstValueFrom(
    phonePrefixesService.apiPhonePrefixesGet(),
  );
  return phonePrefixes.find((p) => p.prefix === prefix) ?? null;
}

export async function processEndorsementOverview(
  endorsementService: EndorsementService,
  contextId: string,
  email: string,
  estSigne: boolean,
  avenants: Avenant[],
) {
  const observables = avenants.map((avenant) =>
    endorsementService.apiEndorsementOverviewGet(
      avenant.agreementId,
      Guid.parse(contextId)?.toJSON().value,
      email,
    ),
  );

  console.log("Created observables for endorsement overview:", observables);

  // // Attendre que toutes les requêtes soient terminées
  return await lastValueFrom(forkJoin(observables));
}

export function dispatchEndorsementOverview(
  store: any,
  overview: AvenantOverviewResponse | undefined,
  avenants: Avenant[],
) {
  
  store.updateContract({
    NumeroContrat: (overview?.agreementId ?? avenants[0]?.agreementId)?.toString(),
    RefContrat: overview?.reference ?? avenants[0]?.reference,
  });

  store.updateSubscriber({
    email: overview?.memberEmail ?? avenants[0]?.email,
  });

  store.updatePhonePrefixSubscriber({
    indicator: { prefix: overview?.memberPhonePrefix ?? '' },
    indicatorString: overview?.memberPhonePrefix ?? '',
    phone: overview?.memberPhoneNumber ?? '',
    phone_consent: false,
  });

  store.updatePhonePrefixDirectDebit({
    indicator: { prefix: overview?.memberPaymentPhonePrefix ?? '' },
    indicatorString: overview?.memberPaymentPhonePrefix ?? '',
    phone: overview?.memberPaymentPhoneNumber ?? '',
    phone_consent: false,
  });
}
